import { usePathname } from 'expo-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Platform, ScrollView, useWindowDimensions } from 'react-native'
import { Searchbar } from 'react-native-paper'
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleSheet } from 'react-native-unistyles'

import SearchResults from '@/components/search/SearchResults'

const RESULTS_MAX_HEIGHT = 800
const TIMING_OPEN = { duration: 250, easing: Easing.out(Easing.cubic) }
const TIMING_CLOSE = { duration: 200, easing: Easing.in(Easing.cubic) }

const DesktopSearchBar = () => {
  const searchBarRef = useRef<any>(null)
  const resultsRef = useRef<Animated.View>(null)
  const contentHeightRef = useRef(0)
  const wasOpenRef = useRef(false)
  const pathname = usePathname()

  const [searchInput, setSearchQuery] = useState('')
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false)
  const [isResultsMounted, setIsResultsMounted] = useState(false)

  const { width } = useWindowDimensions()
  const { left, right } = useSafeAreaInsets()

  const animatedWidth = useSharedValue(200)
  const paneHeight = useSharedValue(0)
  const contentOpacity = useSharedValue(0)

  const showResults = !!(searchInput && isSearchBarFocused)

  const handleContentSizeChange = useCallback(
    (_w: number, h: number) => {
      const rounded = Math.ceil(h)
      if (rounded !== contentHeightRef.current) {
        contentHeightRef.current = rounded
        if (wasOpenRef.current) {
          paneHeight.value = withTiming(
            Math.min(rounded, RESULTS_MAX_HEIGHT),
            TIMING_OPEN
          )
        }
      }
    },
    [paneHeight]
  )

  useEffect(() => {
    if (showResults) {
      setIsResultsMounted(true)
      contentHeightRef.current = 0
      if (!wasOpenRef.current) {
        contentOpacity.value = withTiming(1, TIMING_OPEN)
      }
      wasOpenRef.current = true
    } else if (wasOpenRef.current) {
      contentOpacity.value = withTiming(0, { duration: 150 })
      paneHeight.value = withTiming(0, TIMING_CLOSE, (finished) => {
        if (finished) runOnJS(setIsResultsMounted)(false)
      })
      wasOpenRef.current = false
    }
  }, [showResults, paneHeight, contentOpacity])

  useEffect(() => {
    if (!isResultsMounted || Platform.OS !== 'web') return
    const node = resultsRef.current as unknown as HTMLElement | null
    if (!node) return
    const preventBlur = (e: Event) => e.preventDefault()
    node.addEventListener('mousedown', preventBlur)
    return () => node.removeEventListener('mousedown', preventBlur)
  }, [isResultsMounted])

  useEffect(() => {
    setIsSearchBarFocused(false)
    searchBarRef.current?.blur()
  }, [pathname])

  useEffect(() => {
    const actualWidth = width - left - right - 30
    const maxWidth = width > 500 ? 500 : actualWidth
    if (isSearchBarFocused) {
      animatedWidth.value = withTiming(maxWidth, TIMING_OPEN)
    } else if (!isSearchBarFocused && searchInput === '') {
      animatedWidth.value = withTiming(150, TIMING_OPEN)
    }
  }, [animatedWidth, isSearchBarFocused, left, right, searchInput, width])

  const searchBarAnimatedStyle = useAnimatedStyle(() => ({
    width: animatedWidth.value,
  }))

  const resultsPaneStyle = useAnimatedStyle(() => ({
    height: paneHeight.value,
    overflow: 'hidden' as const,
  }))

  const resultsContentStyle = useAnimatedStyle(() => ({
    opacity: contentOpacity.value,
  }))

  return (
    <>
      <Animated.View style={searchBarAnimatedStyle}>
        <Searchbar
          onBlur={() => setIsSearchBarFocused(false)}
          onChangeText={setSearchQuery}
          onFocus={() => setIsSearchBarFocused(true)}
          placeholder="Search"
          ref={searchBarRef}
          style={styles.searchBar}
          value={searchInput}
        />
      </Animated.View>
      {isResultsMounted && (
        <Animated.View
          ref={resultsRef}
          style={[styles.results, resultsPaneStyle]}
        >
          <ScrollView
            contentContainerStyle={styles.resultsContent}
            onContentSizeChange={handleContentSizeChange}
          >
            <Animated.View style={resultsContentStyle}>
              <SearchResults searchInput={searchInput} />
            </Animated.View>
          </ScrollView>
        </Animated.View>
      )}
    </>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    results: {
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.outline,
      borderRadius: theme.tokens.containerBorderRadius,
      borderWidth: 1,
      elevation: 8,
      position: 'absolute',
      shadowColor: theme.colors.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.15,
      shadowRadius: 8,
      top: 60,
      width: '100%',
      zIndex: 1001,
    },
    resultsContent: {
      padding: theme.tokens.spacing[4],
    },
    searchBar: {
      maxWidth: '100%',
    },
  }
})

export default DesktopSearchBar
